import { type ReactNode, useEffect } from "react";
import { I18nProvider, RouterProvider } from "react-aria-components";
import { Toaster } from "../kit/Toast";

/** Persian locale with the Solar Hijri calendar for every React Aria component (dates, calendars, overlays). */
export const APP_LOCALE = "fa-IR-u-ca-persian";

export interface AppProvidersProps {
    children: ReactNode;
    /**
     * Client-side navigation for React Aria links (Untitled UI buttons, nav items).
     * Pass react-router's `navigate` and `useHref` from inside the router.
     */
    navigate?: (path: string) => void;
    useHref?: (href: string) => string;
}

/** `https://…`, `mailto:`, `tel:` or `//host`: never a route of this app. */
const EXTERNAL_HREF = /^([a-z][a-z0-9+.-]*:|\/\/)/i;

/**
 * React Router's useHref resolves every href against the current route, so an absolute
 * URL (my.mizbanfa.net) became «/login/https:/my.mizbanfa.net/…». The hook still runs on
 * every call (rules of hooks); external hrefs keep their own value.
 */
function useRouteHref(resolve: (href: string) => string, href: string): string {
    const routed = resolve(href);
    return EXTERNAL_HREF.test(href) ? href : routed;
}

/**
 * Root providers shared by both apps. React Aria takes direction from its locale, not from
 * `<html dir>`, so without this provider popovers render LTR in an English browser.
 */
export const AppProviders = ({ children, navigate, useHref }: AppProvidersProps) => {
    useEffect(() => {
        const root = document.documentElement;
        root.lang = "fa";
        root.dir = "rtl";
    }, []);

    const useLinkHref = useHref ? (href: string) => useRouteHref(useHref, href) : undefined;
    const content = navigate ? (
        <RouterProvider navigate={navigate} useHref={useLinkHref}>
            {children}
        </RouterProvider>
    ) : (
        children
    );

    return (
        <I18nProvider locale={APP_LOCALE}>
            {content}
            <Toaster />
        </I18nProvider>
    );
};
